import { Bell } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { App_Url_New } from "@/shared/common/static";
import { IconButton } from "@/shared/components";
import { formatDateTime } from "@/shared/lib/format";

import {
  fetchNotifications,
  markAllRead,
  markRead,
  type AppNotification,
} from "./notificationService";

const POLL_MS = 60000;

/** Header bell: unread count, the latest notifications, mark read. */
export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const feed = await fetchNotifications(10);
      setItems(feed?.items ?? []);
      setUnread(feed?.unread ?? 0);
    } catch {
      // The bell is a convenience; a failed poll is not worth a toast.
    }
  }, []);

  useEffect(() => {
    const first = window.setTimeout(() => void load(), 0);
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (item: AppNotification) => {
    if (!item.read_at) {
      await markRead(item.id).catch(() => undefined);
      void load();
    }
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <div ref={containerRef} className="relative">
      <IconButton
        aria-label={unread ? `${unread} unread notifications` : "Notifications"}
        onClick={() => setOpen((v) => !v)}
        icon={<Bell size={20} />}
      />
      {unread ? (
        <span className="pointer-events-none absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-theme-danger px-1 text-center text-[10px] font-bold leading-[18px] text-theme-text-inverse">
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-xl border border-theme-border bg-theme-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-theme-border px-3 py-2">
            <span className="text-sm font-semibold text-theme-text">Notifications</span>
            <button
              type="button"
              className="text-xs text-theme-accent"
              onClick={async () => {
                await markAllRead().catch(() => undefined);
                void load();
              }}
            >
              Mark all read
            </button>
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-theme-text-soft">Nothing yet.</li>
            ) : null}
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => void openItem(item)}
                  className={`w-full px-3 py-2 text-left hover:bg-theme-surface-soft ${item.read_at ? "" : "bg-theme-accent-soft/40"}`}
                >
                  <div className="text-sm text-theme-text">{item.title}</div>
                  {item.body ? (
                    <div className="line-clamp-2 text-xs text-theme-text-soft">{item.body}</div>
                  ) : null}
                  <div className="mt-0.5 text-[10px] text-theme-text-soft">
                    {formatDateTime(item.created_at)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-theme-border px-3 py-2 text-right">
            <button
              type="button"
              className="text-xs text-theme-accent"
              onClick={() => {
                setOpen(false);
                navigate(App_Url_New.secured.frontlinks.notifications);
              }}
            >
              See all
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
